import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { safetyPlanService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import { Button, Card, Textarea, Badge } from '../components/ui';

const listToText = (items) => (items || []).join('\n');

const SafetyPlan = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [formData, setFormData] = useState({
    warningSigns: '',
    copingStrategies: '',
    peopleForDistraction: '',
    peopleToContact: '',
    professionals: '',
    environmentSafety: ''
  });

  useEffect(() => {
    const loadSafetyPlan = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const response = await safetyPlanService.getSafetyPlan(user.id);
        const plan = response.data.data.plan;
        if (plan) {
          setFormData({
            warningSigns: listToText(plan.warningSigns),
            copingStrategies: listToText(plan.copingStrategies),
            peopleForDistraction: listToText(plan.peopleForDistraction),
            peopleToContact: listToText(plan.peopleToContact),
            professionals: listToText(plan.professionals),
            environmentSafety: listToText(plan.environmentSafety)
          });
        }
      } catch (error) {
        console.error('Failed to load safety plan:', error);
        setMessage({ type: 'error', text: 'Failed to load safety plan.' });
      } finally {
        setLoading(false);
      }
    };

    loadSafetyPlan();
  }, [user?.id]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      await safetyPlanService.saveSafetyPlan(user.id, formData);
      setMessage({ type: 'success', text: 'Safety plan saved.' });
    } catch (error) {
      console.error('Failed to save safety plan:', error);
      setMessage({ type: 'error', text: 'Failed to save safety plan.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading safety plan..." />;
  }

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-6">
          <Badge variant="warning" size="sm">Safety Plan</Badge>
          <h2 className="mt-3 text-3xl font-display font-semibold text-neutral-900">
            Personal Safety Plan
          </h2>
          <p className="mt-2 text-neutral-600">
            Build a quick-access plan for tough moments. Use one line per item.
          </p>
        </div>

        {message && (
          <div
            className={`mb-6 rounded-soft border p-4 text-sm ${
              message.type === 'success'
                ? 'border-success-200 bg-success-50 text-success-700'
                : 'border-error-200 bg-error-50 text-error-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <Card colorVariant="amber" borderAccent="amber">
          <div className="grid gap-4 md:grid-cols-2">
            <Textarea
              label="Warning signs"
              rows={5}
              value={formData.warningSigns}
              onChange={(e) => handleChange('warningSigns', e.target.value)}
              placeholder="Example: skipping meals, isolation, racing thoughts"
              fullWidth
            />
            <Textarea
              label="Internal coping strategies"
              rows={5}
              value={formData.copingStrategies}
              onChange={(e) => handleChange('copingStrategies', e.target.value)}
              placeholder="Example: breathing, grounding, short walk"
              fullWidth
            />
            <Textarea
              label="People or places for distraction"
              rows={5}
              value={formData.peopleForDistraction}
              onChange={(e) => handleChange('peopleForDistraction', e.target.value)}
              placeholder="Example: friend, coffee shop, park"
              fullWidth
            />
            <Textarea
              label="People to contact for help"
              rows={5}
              value={formData.peopleToContact}
              onChange={(e) => handleChange('peopleToContact', e.target.value)}
              placeholder="Example: clinician, therapist, trusted friend, family member"
              fullWidth
            />
            <Textarea
              label="Professionals and emergency resources"
              rows={5}
              value={formData.professionals}
              onChange={(e) => handleChange('professionals', e.target.value)}
              placeholder="Example: therapist, 988, local crisis line"
              fullWidth
            />
            <Textarea
              label="Environment safety steps"
              rows={5}
              value={formData.environmentSafety}
              onChange={(e) => handleChange('environmentSafety', e.target.value)}
              placeholder="Example: adjust environment, remove stressors, create a calming space"
              fullWidth
            />
          </div>

          <div className="mt-6">
            <Button variant="amber" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Safety Plan'}
            </Button>
          </div>
        </Card>
      </main>
    </>
  );
};

export default SafetyPlan;
